import { useTranslation } from 'react-i18next';
import {
  GenericJSONView,
  chipStyle,
  formatSeconds,
  mutedStyle,
  pillStyle,
  useUnserializableLabel,
} from './AIClipRecordView';
import {
  SEGMENT_PREFIXES,
  droppedItemCount,
  formatJSON,
  isFiniteNumber,
  objectItems,
  textOf,
  translateSegment,
} from './aiClipSafeRender';

/**
 * AI剪辑软警告清单（warnings.json schemaVersion 1）的语义化渲染：
 * 摘要 chips + planWarnings 按 gate 分组 + 评论处理 + 转写缺失台词；
 * schema 不匹配回退通用 JSON 折叠树。所有自由文本为不可信内容，一律
 * 走 JSX 文本节点输出（KataBit 契约要求，禁止 innerHTML 类注入点）。
 */

interface PlanWarning {
  gate?: string;
  assetId?: string;
  startSeconds?: number;
  endSeconds?: number;
  detail?: unknown;
}

interface CommentResolution {
  commentKey?: string;
  status?: string;
  scriptAnchor?: string;
  reason?: string;
}

interface MissingLine {
  shotIndex?: number;
  assetId?: string;
  startSeconds?: number;
  endSeconds?: number;
  text?: string;
}

interface WarningsV1 {
  schemaVersion: number;
  // 三个数组均可能是 null（Go nil slice）；取值一律经 objectItems 归一化
  planWarnings?: PlanWarning[] | null;
  commentResolutions?: CommentResolution[] | null;
  transcriptAlignment: {
    applicable?: boolean;
    rate?: number;
    floor?: number;
    missingLines?: MissingLine[] | null;
  };
}

/**
 * 三个警告数组缺省或为 null（Go 的 nil slice 序列化即 null）都算合法契约；
 * 其它非数组类型一律判为 schema 不匹配，回退通用 JSON 树——否则 `.map` 会抛错，
 * 本仓没有 ErrorBoundary，一次抛错整棵 root 卸载 = 整页白屏。
 */
const isOptionalArray = (value: unknown): boolean => value == null || Array.isArray(value);

export function isWarningsV1(value: unknown): value is WarningsV1 {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (v.schemaVersion !== 1) return false;
  if (!isOptionalArray(v.planWarnings) || !isOptionalArray(v.commentResolutions)) return false;
  const alignment = v.transcriptAlignment;
  // 数组也满足 typeof === 'object'，但 alignment 是数组时整个负载结构已错，
  // 不能当合法 schema 渲染出「无警告」的确定性结论。
  if (typeof alignment !== 'object' || alignment === null || Array.isArray(alignment)) return false;
  return isOptionalArray((alignment as Record<string, unknown>).missingLines);
}

/** 不可信 payload 归一化，渲染与角标计数共用同一份结果。 */
function normalizeWarnings(value: WarningsV1) {
  const alignment = value.transcriptAlignment;
  const planWarnings = objectItems<PlanWarning>(value.planWarnings);
  const commentResolutions = objectItems<CommentResolution>(value.commentResolutions);
  const isAlignmentNA = alignment.applicable === false;
  const missingLines = isAlignmentNA
    ? []
    : objectItems<MissingLine>(alignment.missingLines);
  // objectItems 静默丢掉非对象元素。全是垃圾元素时 total 会是 0，直接渲染绿色「无警告」
  // 就等于对一份结构已错的负载给出「一切正常」的确定性结论（与 transcriptAlignment
  // 拒数组的理由自相矛盾）。这里把丢弃量带出去，由调用方改用「负载异常」提示。
  // applicable === false 时 missingLines 按契约整体忽略，不算「无法解析」。
  const dropped = droppedItemCount(value.planWarnings, planWarnings)
    + droppedItemCount(value.commentResolutions, commentResolutions)
    + (isAlignmentNA ? 0 : droppedItemCount(alignment.missingLines, missingLines));
  return {
    planWarnings,
    commentResolutions,
    missingLines,
    dropped,
    total: planWarnings.length + commentResolutions.length + missingLines.length,
  };
}

/** 警告总数（tab 角标用）；schema 不匹配返回 null（不显示角标）。 */
export function countWarnings(value: unknown): number | null {
  if (!isWarningsV1(value)) return null;
  return normalizeWarnings(value).total;
}

/**
 * 被归一化静默丢弃的元素数（tab 角标用）；schema 不匹配返回 0。
 *
 * 与 countWarnings 并列导出而非合并返回值：total 为 0 但 dropped > 0 时，
 * 角标若只看 total 就完全不出现，用户没有线索去点开「警告」tab 看那条
 * 「负载异常」提示——等于把结构已错的负载悄悄渲染成「看着没事」。
 */
export function countDroppedWarnings(value: unknown): number {
  if (!isWarningsV1(value)) return 0;
  return normalizeWarnings(value).dropped;
}

/**
 * detail 人读文案：优先 detail.message，其次字符串本身，否则整体序列化。
 * 序列化走 formatJSON（深嵌套 detail 会让裸 JSON.stringify 抛 RangeError = 整页白屏）。
 */
const detailText = (detail: unknown, unserializable: string): string => {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    const message = textOf((detail as Record<string, unknown>).message);
    if (message) return message;
  }
  return formatJSON(detail, unserializable, 0);
};

const groupByGate = (warnings: PlanWarning[]): [string, PlanWarning[]][] => {
  const groups = new Map<string, PlanWarning[]>();
  warnings.forEach(w => {
    const gate = textOf(w.gate) || 'unknown';
    groups.set(gate, [...(groups.get(gate) || []), w]);
  });
  return Array.from(groups.entries());
};

const itemStyle: React.CSSProperties = {
  padding: '0.3rem 0',
  borderBottom: '1px dashed var(--border)',
};

const sectionTitleStyle: React.CSSProperties = {
  cursor: 'pointer',
  fontSize: '0.8rem',
  color: 'var(--text-2)',
};

function PlanWarningItem({ warning }: { warning: PlanWarning }) {
  const { t } = useTranslation('episodeDetail');
  const unserializable = useUnserializableLabel();
  const assetId = textOf(warning.assetId);
  // 严格 0/0 才是「整集级」：秒数缺失属于「区间未知」，落回 formatSeconds 的 ?–?
  const isEpisodeLevel = !assetId
    && warning.startSeconds === 0
    && warning.endSeconds === 0;
  return (
    <div style={itemStyle}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.15rem' }}>
        {assetId && <span style={chipStyle}>{assetId}</span>}
        {isEpisodeLevel ? (
          <span style={chipStyle}>{t('aiClip.warnings.episodeLevel', { defaultValue: '整集' })}</span>
        ) : (
          <span style={chipStyle}>
            {formatSeconds(warning.startSeconds)}–{formatSeconds(warning.endSeconds)}
          </span>
        )}
      </div>
      <div style={mutedStyle}>{detailText(warning.detail, unserializable)}</div>
    </div>
  );
}

function PlanWarningsSection({ warnings }: { warnings: PlanWarning[] }) {
  const { t } = useTranslation('episodeDetail');
  if (warnings.length === 0) return null;
  return (
    <div>
      {groupByGate(warnings).map(([gate, items], index) => (
        <details key={gate} open={index === 0}>
          <summary style={sectionTitleStyle}>
            {/* 不可信 gate 不能进 i18next 处理管线（defaultValue 仍跑插值与 $t() 嵌套，
                会让未知 gate 伪装成本产品文案），也不能无条件当 key（原型链名字会让
                t() 返回诊断串或 Function）；两种情况都经 JSX 转义原样输出。 */}
            {translateSegment(t, SEGMENT_PREFIXES.warningsGate, gate)} ×{items.length}
          </summary>
          <div style={{ paddingLeft: '0.25rem' }}>
            {items.map((w, i) => <PlanWarningItem key={i} warning={w} />)}
          </div>
        </details>
      ))}
    </div>
  );
}

function CommentsSection({ comments }: { comments: CommentResolution[] }) {
  const { t } = useTranslation('episodeDetail');
  if (comments.length === 0) return null;
  return (
    <details open>
      <summary style={sectionTitleStyle}>
        {t('aiClip.warnings.commentsTitle', { count: comments.length, defaultValue: `评论处理（${comments.length}）` })}
      </summary>
      <div style={{ paddingLeft: '0.25rem' }}>
        {comments.map((c, i) => {
          const status = textOf(c.status);
          const commentKey = textOf(c.commentKey);
          const scriptAnchor = textOf(c.scriptAnchor);
          const reason = textOf(c.reason);
          return (
            <div key={i} style={itemStyle}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center', marginBottom: '0.15rem' }}>
                {status && (
                  <span style={pillStyle('var(--warning, #b45309)', 'rgba(180, 83, 9, 0.12)')}>
                    {/* 同 gate：不可信 status 未命中译名（或不是安全 key 片段）时原样输出 */}
                    {translateSegment(t, SEGMENT_PREFIXES.warningsCommentStatus, status)}
                  </span>
                )}
                {commentKey && <span style={chipStyle}>{commentKey}</span>}
              </div>
              {scriptAnchor && <div style={mutedStyle}>{scriptAnchor}</div>}
              {reason && <div style={mutedStyle}>{reason}</div>}
            </div>
          );
        })}
      </div>
    </details>
  );
}

function MissingLinesSection({ lines }: { lines: MissingLine[] }) {
  const { t } = useTranslation('episodeDetail');
  if (lines.length === 0) return null;
  return (
    <details open>
      <summary style={sectionTitleStyle}>
        {t('aiClip.warnings.missingLinesTitle', { count: lines.length, defaultValue: `成片缺失台词（${lines.length}）` })}
      </summary>
      <div style={{ paddingLeft: '0.25rem' }}>
        {lines.map((line, i) => {
          const assetId = textOf(line.assetId);
          return (
            <div key={i} style={itemStyle}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.15rem' }}>
                {/* NaN / Infinity 也满足 typeof === 'number'，会渲染成「镜头 NaN」 */}
                {isFiniteNumber(line.shotIndex) && (
                  <span style={chipStyle}>
                    {t('aiClip.record.shotLabel', { index: line.shotIndex, defaultValue: `镜头 ${line.shotIndex}` })}
                  </span>
                )}
                {assetId && <span style={chipStyle}>{assetId}</span>}
                <span style={chipStyle}>
                  {formatSeconds(line.startSeconds)}–{formatSeconds(line.endSeconds)}
                </span>
              </div>
              <div style={mutedStyle}>{textOf(line.text)}</div>
            </div>
          );
        })}
      </div>
    </details>
  );
}

export default function AIClipWarningsView({ warnings }: { warnings: unknown }) {
  const { t } = useTranslation('episodeDetail');
  const unserializable = useUnserializableLabel();

  if (!isWarningsV1(warnings)) {
    return <GenericJSONView record={warnings} />;
  }

  const alignment = warnings.transcriptAlignment;
  const { planWarnings, commentResolutions, missingLines, dropped, total } = normalizeWarnings(warnings);
  const rate = alignment.rate;

  const malformedPill = dropped > 0 && (
    <span style={pillStyle('var(--warning, #b45309)', 'rgba(180, 83, 9, 0.12)')}>
      {t('aiClip.warnings.malformed', {
        count: dropped,
        defaultValue: `负载异常：${dropped} 条记录无法解析`,
      })}
    </span>
  );

  // spec：全部为空时整块只显示绿色「无警告」胶囊；但若有元素被静默丢弃，
  // 「无警告」就是对一份结构已错的负载给出「一切正常」的结论 —— 改报负载异常。
  if (total === 0) {
    return malformedPill || (
      <span style={pillStyle('var(--success, #1a7f37)', 'rgba(26, 127, 55, 0.1)')}>
        {t('aiClip.warnings.none', { defaultValue: '无警告' })}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
        <span style={pillStyle('var(--warning, #b45309)', 'rgba(180, 83, 9, 0.12)')}>
          {t('aiClip.warnings.total', { count: total, defaultValue: `${total} 条警告` })}
        </span>
        {malformedPill}
        {alignment.applicable === false ? (
          <span style={chipStyle}>{t('aiClip.warnings.alignmentNA', { defaultValue: '转写对齐不适用' })}</span>
        ) : (
          isFiniteNumber(rate) && (
            <span style={chipStyle}>
              {t('aiClip.warnings.alignmentRate', {
                rate: (rate * 100).toFixed(1),
                defaultValue: `转写对齐 ${(rate * 100).toFixed(1)}%`,
              })}
            </span>
          )
        )}
      </div>

      <PlanWarningsSection warnings={planWarnings} />
      <CommentsSection comments={commentResolutions} />
      <MissingLinesSection lines={missingLines} />

      <details style={{ marginTop: '0.25rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-3)' }}>
          {t('aiClip.record.rawJson', { defaultValue: '查看原始 JSON' })}
        </summary>
        <pre style={{ fontSize: '0.74rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {formatJSON(warnings, unserializable)}
        </pre>
      </details>
    </div>
  );
}
